import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { DEPARTMENTS } from "../components/SubmissionModal";
import api from "../lib/api";

const OTHER = "__other__";

const inputCls =
  "w-full rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-800 placeholder-slate-400 outline-none transition-colors focus:border-[#2563EB] focus:bg-white";
const labelCls = "mb-2 block font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400";

export default function AuthPage() {
  const { login, signup } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next");

  const [tab, setTab] = useState(next === "submit" ? "signup" : "login");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [form, setForm] = useState({ name: "", student_id: "", email: "", department: "", other_department: "", level: "undergraduate", password: "" });
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);

  const done = () => {
    if (next === "submit") navigate("/?open_submit=1");
    else if (next === "my") navigate("/my-projects");
    else navigate("/");
  };

  const doLogin = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const u = await login(loginForm.email, loginForm.password);
      toast.success(`Welcome back, ${u.name}`);
      if (u.role === "admin") { navigate("/admin"); return; }
      done();
    } catch (err) {
      setError(err.response?.data?.detail || "Login failed");
    } finally { setBusy(false); }
  };

  const doForgot = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await api.post("/auth/forgot", { email: forgotEmail });
      setForgotSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Request failed");
    } finally { setBusy(false); }
  };

  const doSignup = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    const department = form.department === OTHER ? form.other_department.trim() : form.department;
    if (!department) { setError(t.sub_f_dept + " required"); setBusy(false); return; }
    try {
      const u = await signup({ name: form.name, student_id: form.student_id, email: form.email, department, level: form.level, password: form.password });
      toast.success(`Welcome to Keimyung, ${u.name}`);
      done();
    } catch (err) {
      setError(err.response?.data?.detail || "Signup failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="flex min-h-[80vh] items-center justify-center px-5 pb-16 pt-28" data-testid="auth-page">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-md rounded-xl border border-slate-200/90 bg-white p-8 shadow-[0_12px_40px_-8px_rgba(15,23,42,0.1)] sm:p-9"
      >
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#2563EB]">{t.login_tag}</p>
        <h1 className="mt-2 font-display text-2xl font-bold text-slate-900">{tab === "forgot" ? t.auth_forgot_title : t.auth_portal}</h1>
        {tab === "forgot" && <p className="mt-1 text-sm text-slate-500">{t.auth_forgot_sub}</p>}
        {next === "submit" && tab !== "forgot" && <p className="mt-1 text-sm text-slate-500">{t.auth_need}</p>}

        {tab !== "forgot" && (
        <div className="mt-6 grid grid-cols-2 gap-1 rounded-lg bg-slate-100 p-1" data-testid="auth-tabs">
          {[["login", t.auth_login_tab], ["signup", t.auth_signup_tab]].map(([key, label]) => (
            <button
              key={key}
              onClick={() => { setTab(key); setError(""); }}
              data-testid={`auth-tab-${key}`}
              className={`rounded-md py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] transition-all duration-300 ${
                tab === key ? "bg-[#1E3A8A] text-white shadow" : "text-slate-500 hover:text-slate-800"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        )}

        {tab === "forgot" ? (
          forgotSent ? (
            <div className="mt-6 space-y-4" data-testid="forgot-sent">
              <p className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700" data-testid="forgot-sent-message">
                {t.auth_forgot_sent}
              </p>
              <button type="button" onClick={() => { setTab("login"); setError(""); setForgotSent(false); }} data-testid="forgot-back-button" className="w-full text-center font-mono text-[11px] uppercase tracking-[0.15em] text-[#2563EB] transition-colors hover:text-[#1E3A8A]">
                {t.auth_back_login}
              </button>
            </div>
          ) : (
            <form onSubmit={doForgot} className="mt-6 space-y-4" data-testid="forgot-form">
              <input type="email" required autoComplete="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} data-testid="forgot-email-input" placeholder={t.auth_email} className={inputCls} />
              {error && <p className="text-sm text-rose-500" data-testid="auth-error">{error}</p>}
              <button type="submit" disabled={busy} data-testid="forgot-submit-button" className="w-full rounded-md bg-[#1E3A8A] py-3.5 font-mono text-xs uppercase tracking-[0.18em] text-white transition-colors hover:bg-[#2563EB] disabled:opacity-50">
                {busy ? t.login_busy : t.auth_forgot_btn}
              </button>
              <button type="button" onClick={() => { setTab("login"); setError(""); }} data-testid="forgot-back-button" className="w-full text-center font-mono text-[11px] uppercase tracking-[0.15em] text-slate-500 transition-colors hover:text-slate-800">
                {t.auth_back_login}
              </button>
            </form>
          )
        ) : tab === "login" ? (
          <form onSubmit={doLogin} className="mt-6 space-y-4" data-testid="login-form">
            <input type="text" required autoComplete="username" value={loginForm.email} onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })} data-testid="login-email-input" placeholder={t.auth_email} className={inputCls} />
            <input type="password" required autoComplete="current-password" value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} data-testid="login-password-input" placeholder={t.auth_pw} className={inputCls} />
            {error && <p className="text-sm text-rose-500" data-testid="auth-error">{error}</p>}
            <button type="submit" disabled={busy} data-testid="login-submit-button" className="w-full rounded-md bg-[#1E3A8A] py-3.5 font-mono text-xs uppercase tracking-[0.18em] text-white transition-colors hover:bg-[#2563EB] disabled:opacity-50">
              {busy ? t.login_busy : t.login_btn}
            </button>
            <button type="button" onClick={() => { setTab("forgot"); setError(""); setForgotSent(false); }} data-testid="forgot-password-link" className="w-full text-center font-mono text-[11px] uppercase tracking-[0.15em] text-slate-500 transition-colors hover:text-[#2563EB]">
              {t.auth_forgot}
            </button>
          </form>
        ) : (
          <form onSubmit={doSignup} className="mt-6 space-y-4" data-testid="signup-form">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className={labelCls}>{t.auth_name} *</label>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="signup-name-input" placeholder="Jihun Park" className={inputCls} />
              </div>
            </div>
            <div>
              <label className={labelCls}>{t.auth_email} *</label>
              <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="signup-email-input" placeholder="you@kmu.ac.kr" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>{t.auth_dept} *</label>
              <select value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} data-testid="signup-department-select" className={inputCls}>
                <option value="">{t.sub_f_dept_ph}</option>
                {DEPARTMENTS.map((d) => <option key={d} value={d}>{d}</option>)}
                <option value={OTHER}>{t.sub_f_other}</option>
              </select>
              {form.department === OTHER && (
                <input required value={form.other_department} onChange={(e) => setForm({ ...form, other_department: e.target.value })} data-testid="signup-other-department-input" placeholder={t.sub_f_other_ph} className={`${inputCls} mt-3`} />
              )}
            </div>
            <div>
              <label className={labelCls}>{t.auth_level} *</label>
              <div className="grid grid-cols-2 gap-3" data-testid="signup-level-group">
                {[["undergraduate", t.auth_ug], ["graduate", t.auth_g], ["faculty", t.auth_faculty], ["staff", t.auth_staff]].map(([val, label]) => (
                  <button
                    type="button"
                    key={val}
                    onClick={() => setForm({ ...form, level: val })}
                    data-testid={`signup-level-${val}`}
                    className={`rounded-md border px-4 py-3 text-sm font-medium transition-all duration-300 ${
                      form.level === val ? "border-[#1E3A8A] bg-[#1E3A8A] text-white" : "border-slate-200 bg-slate-50 text-slate-600 hover:border-[#2563EB]/50"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className={labelCls}>{t.auth_pw} * (min 6)</label>
              <input type="password" required minLength={6} autoComplete="new-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="signup-password-input" placeholder="••••••••" className={inputCls} />
            </div>
            {error && <p className="text-sm text-rose-500" data-testid="auth-error">{error}</p>}
            <button type="submit" disabled={busy} data-testid="signup-submit-button" className="w-full rounded-md bg-emerald-600 py-3.5 font-mono text-xs uppercase tracking-[0.18em] text-white transition-colors hover:bg-emerald-500 disabled:opacity-50">
              {busy ? t.auth_creating : t.auth_create}
            </button>
          </form>
        )}
      </motion.div>
    </div>
  );
}
